import { Box, Button, ButtonDescription, tokens } from "@vp/swan";
import UploadFlow from "@vp/upload-flow";
import React, { FunctionComponent, useCallback, useContext } from "react";
import PageConfig from "../../../../utils/PageConfig";
import { BffDataContext } from "../../../../contexts/BffDataContextProvider";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { PricingContext } from "../../../../contexts/PricingContext";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
import { analyticsPageData } from "../../../../utils/analyticsConfig";
import { CtaCustomData } from "../CtasContainer/CtasContainer";
import { PluginDataContext } from "../../../../pluginsInfrastructure/PluginDataContextProvider";
import { getCtaOnClickHandler, getProductCtaInfo } from "../../utils/ctaHelpers";
import useGetStudioDocumentUrl from "../../../../hooks/useGetStudioDocumentUrl";
import { useRequiredTextboxFieldsWithError } from "../../../../hooks/useRequiredTextboxFieldsWithError.hook";
import { IdentityContext } from "../../../../contexts/IdentityProvider";

export interface UploadFlowCtaProps {
  customData: CtaCustomData;
  siteflowAction?: string;
}

export const UploadCtaClickedEventName = "UploadCtaClicked";

const UploadFlowCta: FunctionComponent<UploadFlowCtaProps> = (props: UploadFlowCtaProps) => {
  const {
    customData: {
      flow: { type, context },
      isPrimaryCta,
      buttonIndex,
      showDescription,
      isDesignUploaded,
      isLastCta,
    },
    siteflowAction,
  } = props;
  const { auth } = useContext(IdentityContext);
  const { pluginData } = useContext(PluginDataContext);
  const { locale, mpv, ctaOverrideEntries, productBuilderOptions } = useContext(
    PageStaticDataContext
  );
  const { temporarilyOutOfStock, prices, initialPrices, compatibleRanges } = useContext(
    BffDataContext
  );
  const { mpvId, name: productName, coreProductId, version: productVersion } = mpv;
  const {
    selectedQuantity,
    clientSideStateInitialized,
    initialSelections,
    initialQuantity,
    selections,
    updateInProgress,
  } = useContext(ProductContext);
  const { vatInclusivity, encodedPricingContext } = useContext(PricingContext);

  const { actualCtaText, actualCtaDescription, actualCtaIcon } = getProductCtaInfo(
    pluginData,
    ctaOverrideEntries,
    type,
    isDesignUploaded,
    isPrimaryCta
  );

  const ctaEnabled = clientSideStateInitialized && !temporarilyOutOfStock;

  const tenant: string = PageConfig.tenant;
  const requiredFieldsWithError = useRequiredTextboxFieldsWithError();
  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (requiredFieldsWithError.length !== 0) {
        event.stopPropagation();
        event.preventDefault();
      }
      const customEvent = new CustomEvent(UploadCtaClickedEventName, {});
      document.dispatchEvent(customEvent);

      getCtaOnClickHandler({
        event,
        mpv,
        selections: selections || {},
        initialSelections,
        pageSection: analyticsPageData.pageSection,
        position: buttonIndex + 1,
        url: "/studio",
        linkType: "studio",
        initialQuantity,
        selectedQuantity,
        prices,
        productBuilderOptions,
        initialPrices,
        flowContexts: context,
        isDesignUploaded,
        compatibleRanges,
      });
    },
    [
      requiredFieldsWithError,
      mpvId,
      productName,
      coreProductId,
      selections,
      initialSelections,
      initialQuantity,
      selectedQuantity,
      prices,
      initialPrices,
      productBuilderOptions,
      UploadCtaClickedEventName,
      buttonIndex,
      analyticsPageData.pageSection,
    ]
  );

  const { studioDocumentUrl } = useGetStudioDocumentUrl();

  const textButtonProps = {
    skin: isPrimaryCta ? "primary" : "secondary",
  };

  const UploadFlowTextButton = (props: { disabled: boolean; textButtonProps?: any }) => (
    <Button
      component="span"
      aria-disabled={props.disabled}
      specialVariant={"design-path"}
      width="full-width"
      iconPosition="right"
      disabled={!ctaEnabled}
      onClick={onClickHandler}
      {...props.textButtonProps}
      key={actualCtaText}
    >
      {actualCtaText}
      {showDescription && actualCtaDescription && (
        <ButtonDescription className="cta-description">{actualCtaDescription}</ButtonDescription>
      )}
      {actualCtaIcon}
    </Button>
  );

  if (!clientSideStateInitialized || updateInProgress) {
    return (
      <div key={"cta-/studio"}>
        {<UploadFlowTextButton textButtonProps={textButtonProps} disabled={true} />}
      </div>
    );
  }

  return (
    <Box key={"cta-/studio"} marginBottom={isLastCta ? "0" : "3"}>
      <UploadFlow
        vatInc={vatInclusivity}
        analyticsPageData={{
          pageSection: "Product Page",
          pageStage: "Discover",
          pageName: `${mpvId}:Product Page`,
          productName,
          coreProductId,
          position: buttonIndex + 1,
        }}
        productKey={coreProductId}
        productName={productName}
        productVersion={productVersion}
        pricingContextString={encodedPricingContext}
        tenant={tenant}
        locale={locale}
        quantity={selectedQuantity}
        userOptions={selections}
        mpvId={mpvId}
        siteflowAction={siteflowAction}
        loader={<UploadFlowTextButton textButtonProps={textButtonProps} disabled={true} />}
        // studioDocumentUrl for UploadFlow needs to be just a string,
        // but the useStudioDocumentUrl hook provides an encoded string
        studioDocumentUrl={studioDocumentUrl ? decodeURIComponent(studioDocumentUrl) : undefined}
        authToken={auth?.getToken()}
        requestor={`${PageConfig.requestor}-${PageConfig.isProduction ? "prod" : "sandbox"}`}
      >
        <UploadFlowTextButton textButtonProps={textButtonProps} disabled={false} />
      </UploadFlow>
    </Box>
  );
};

export default UploadFlowCta;
